import type { ReactElement } from 'react'

import type { ContractDtoBidUnitEnum } from '@b-stock/contract-api-client'
import type { OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import {
  useFormatContractBidAmount,
  useFormatSpotBidAmount,
} from '@/buyer-portal/components/SearchCard/formatBidAmount'

const MAX_PERCENT_OFFER_AMOUNT = 100

// A percentage offer is a contract with a RELATIVE bid basis, or a spot offer
// priced as % of MSRP. Shared so the header and offer input can't drift apart.
export const getIsPercentOffer = (
  listing: OrchestratedOfferingListingDetails
): boolean =>
  listing.contract
    ? listing.contract.bidBasis === 'RELATIVE'
    : listing.offering?.offerAmountUnits === 'PERCENT_MSRP'

/**
 * A spot (non-contract) absolute-price offer — the only case that shows the TLC
 * stats, TLC breakdown, and the %-of-MSRP header sub-line. All contract MAO
 * offers (%, COGS, or per-unit) and spot percentage offers omit them per spec.
 * Shared so those surfaces agree.
 */
export const getIsSpotPriceOffer = (
  listing: OrchestratedOfferingListingDetails
): boolean =>
  !listing.contract && listing.offering?.offerAmountUnits === 'PRICE'

/**
 * Offer-amount formatting: PRICE offers render as currency, PERCENT_MSRP as
 * "X% of MSRP". Contracts may also be COGS or "$X per {unit}".
 */
export const useOfferAmount = (listing: OrchestratedOfferingListingDetails) => {
  const { contract, currency = 'USD', offering } = listing
  const bidPrecision = offering?.bidPrecision ?? 1
  const isPercentOffer = getIsPercentOffer(listing)

  const formatContractBidAmount = useFormatContractBidAmount({
    bidBasis: contract?.bidBasis,
    bidPrecision,
    bidUnit: contract?.bidUnit,
    currency,
    pricingStrategy: 'MAKE_AN_OFFER',
  })
  const formatSpotBidAmount = useFormatSpotBidAmount({
    bidPrecision,
    currency,
    offerAmountUnits: offering?.offerAmountUnits,
    pricingStrategy: 'MAKE_AN_OFFER',
  })
  const formatAmount = contract ? formatContractBidAmount : formatSpotBidAmount

  const displayBidUnit: ContractDtoBidUnitEnum | 'MSRP' | null =
    contract?.bidUnit ?? (isPercentOffer ? 'MSRP' : null)

  // Renders the amount with its unit suffix ("X% of MSRP" / "$X per lb"), or
  // bare currency when there is no unit (absolute spot offer). The redesigned
  // card is tight enough that the unit has to be abbreviated, and it sits in
  // running content rather than a title, so it takes sentence case.
  const renderOfferAmount = (value: number): ReactElement | string =>
    displayBidUnit ? (
      <FormattedMessage
        id={`BStockNext.Contracts.Enums.perUnitAmount.shortSentenceCase.${displayBidUnit}`}
        values={{ amount: formatAmount(value) }}
      />
    ) : (
      formatAmount(value)
    )

  const isValidOffer = (amount: number): boolean =>
    !isPercentOffer || amount <= MAX_PERCENT_OFFER_AMOUNT

  return { formatAmount, isPercentOffer, isValidOffer, renderOfferAmount }
}
